import { useCallback, useEffect, useMemo } from 'react';

import { isEmpty } from 'lodash';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IExternalOffersFragment } from 'generated/sanity-graphql';
import { useBatteryLevel } from 'hooks/use-battery-level';
import { UserDetails } from 'state/auth/hooks/types';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EVENT_PAGE_NAMES, EventTypes } from 'state/cdp/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLoyaltyContext } from 'state/loyalty';
import { IUseLoyaltyQuests, IncentiveEvaluationMap, LoyaltyUser } from 'state/loyalty/hooks/types';
import { ICart } from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart/incentive-cart/types';
import { LoyaltyAppliedOffer, LoyaltyOffer } from 'state/loyalty/types';
import { useStoreNearby } from 'state/nearby-stores/hooks';
import { useOrderContext } from 'state/order';
import { BooleanFlags, FlagType } from 'utils/launchdarkly';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { useLoyaltyIncentivesAvailability } from '../loyalty-incentives-components/use-loyalty-incentives-availability';

import { OfferStatus } from './types';
import { getSelectedIncentive, reduceOffers } from './utils';

export interface IUseOfferView {
  enableLoyaltyPromoCodes: any;
  loyaltyUser: LoyaltyUser;
  user: UserDetails | null;
  appliedOffers: LoyaltyAppliedOffer[];
  inRestaurantRedemptionCart: ICart;
  loyaltyEngineId: string | undefined;
  enableGeolocation: FlagType<BooleanFlags>;
  offersLoading: boolean;
  tabPath: string;
  externalOffers: IExternalOffersFragment[];
  challengeIncentives: IUseLoyaltyQuests['challengeIncentives'];
  isMobile: boolean;
  onChallengeIcentiveSelect: (challengeIncentiveSelected: boolean | undefined) => void;
}

export const useOfferView = ({
  enableLoyaltyPromoCodes,
  loyaltyUser,
  user,
  appliedOffers,
  inRestaurantRedemptionCart,
  loyaltyEngineId,
  enableGeolocation,
  offersLoading,
  tabPath,
  externalOffers,
  challengeIncentives,
  isMobile,
  onChallengeIcentiveSelect,
}: IUseOfferView) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loyaltyCmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const { formatMessage } = useIntl();
  const { queryLoyaltyOffers, queryLoyaltyUserStandardOffers } = useLoyaltyContext();
  const { selectFirstNearbyStore } = useStoreNearby();
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const { setEventPageName } = useLocationContext();
  const cdp = useCdpContext();

  const enableSortOffersForServiceMode = useFlag(
    LaunchDarklyFlag.ENABLE_SORT_OFFERS_FOR_SERVICE_MODE
  );
  const order = useOrderContext();

  const userOffers = useAppSelector(selectors.loyalty.selectUserOffers);
  const unauthenticatedOffers = useAppSelector(selectors.loyalty.selectOffers);
  const personalizedOffers = useAppSelector(selectors.loyalty.selectPersonalizedOffers);

  const cartTotal: number = useMemo(
    () => order?.calculateCartTotal && order.calculateCartTotal(),
    [order]
  );
  let loyaltyId = loyaltyUser?.id;
  // Some markets want to use promo-code and have enabled loyalty flag only on the BE.
  // This logic permits use promo code, even when the loyalty flag is not enabled on the FE
  // When the loyalty flag is enabled on the BE, we can get the loyaltyId from the user data
  // instead of the loyaltyUser, which is only set when the loyalty FE flag is enabled
  if (enableLoyaltyPromoCodes) {
    loyaltyId = user?.loyaltyId ?? loyaltyUser?.id;
  }
  const fetchOffers = useCallback(() => {
    if (loyaltyId) {
      queryLoyaltyUserStandardOffers({
        loyaltyId,
        subtotalAmount: cartTotal,
        totalAmount: cartTotal,
      });
    } else {
      queryLoyaltyOffers({ subtotalAmount: cartTotal, totalAmount: cartTotal });
    }
  }, [cartTotal, loyaltyId, queryLoyaltyOffers, queryLoyaltyUserStandardOffers]);

  // Fetch offers on first mount
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const userBatteryLevel = useBatteryLevel();
  const [offers, offersInCart] = useMemo(
    () =>
      !loyaltyCmsOffers
        ? []
        : reduceOffers({
            cmsOffers: loyaltyCmsOffers,
            personalizedOffers,
            baseOffers: loyaltyId ? userOffers : unauthenticatedOffers,
            appliedOffers,
            inRestaurantRedemptionCart,
            checkLoyaltyOfferAvailability,
            enableSortOffersForServiceMode,
            setOffersFeedbackMap: (offerFeedbackMap: IncentiveEvaluationMap) =>
              dispatch(actions.loyalty.setOffersFeedbackMap(offerFeedbackMap)),
            userOfferAttributes: {
              userBatteryLevel,
              dobDeleted: user?.details.dobDeleted,
              phoneNumberVerified: user?.details.phoneVerified,
              promotionalEmails: user?.details.promotionalEmails,
              zipcode: user?.details.zipcode,
              hasDeliveryAddress: !!user?.details.deliveryAddresses?.length,
              favoriteStores: user?.details.favoriteStores?.map(store => store.storeNumber),
              favoriteOffers: user?.details.favoriteOffers?.map(offer => offer.id),
            },
          }),
    [
      loyaltyCmsOffers,
      personalizedOffers,
      loyaltyId,
      userOffers,
      unauthenticatedOffers,
      appliedOffers,
      inRestaurantRedemptionCart,
      checkLoyaltyOfferAvailability,
      enableSortOffersForServiceMode,
      userBatteryLevel,
      user?.details,
      dispatch,
    ]
  );

  const selectedIncentive = getSelectedIncentive(
    loyaltyEngineId,
    offers,
    offersInCart,
    isMobile,
    challengeIncentives,
    externalOffers
  );

  useEffect(() => {
    if (loyaltyEngineId && !selectedIncentive && !offersLoading && !isEmpty(offers)) {
      navigate(`${formatMessage({ id: 'routes.loyaltyHome' })}/${tabPath}`);
      toast.warning(
        formatMessage(
          { id: 'incentiveFeedbackInvalidStore' },
          { incentiveType: formatMessage({ id: 'offer' }) }
        )
      );
    }
  }, [formatMessage, loyaltyEngineId, navigate, offers, offersLoading, selectedIncentive, tabPath]);

  useEffect(() => {
    if (enableGeolocation) {
      selectFirstNearbyStore();
    }
  }, [enableGeolocation, selectFirstNearbyStore]);

  const onPromoCodeRedeemSuccess = (offer: LoyaltyOffer) => {
    toast.success(formatMessage({ id: 'promoCodeAddedToOffers' }));
    dispatch(actions.loyalty.setCmsOffers([offer]));
    dispatch(actions.loyalty.unshiftPersonalizedOffer(offer));
    fetchOffers();
  };

  const handleClick =
    ({
      incentiveName,
      loyaltyEngineId: loyaltyEngineIdProp,
      incentiveSanityId,
      isQuestChallengeIcentive,
      isExternalOffer,
      isUpcomingOffer,
      status,
    }: {
      incentiveName: string;
      loyaltyEngineId: string;
      incentiveSanityId: string;
      isQuestChallengeIcentive?: boolean;
      isExternalOffer?: boolean;
      isUpcomingOffer?: boolean;
      status?: OfferStatus;
    }) =>
    () => {
      if (incentiveSanityId !== selectedIncentive?._id) {
        const navigateID = isExternalOffer ? incentiveSanityId : loyaltyEngineIdProp;
        const eventName = isExternalOffer
          ? CustomEventNames.EXTERNAL_OFFER_SELECTED
          : CustomEventNames.OFFER_SELECTED;

        cdp.trackEvent({
          name: eventName,
          type: EventTypes.Other,
          attributes: {
            engineId: loyaltyEngineIdProp,
            sanityId: incentiveSanityId,
            name: incentiveName,
            status,
          },
        });
        if (isExternalOffer) {
          // Define the page name used in Page View event
          setEventPageName(EVENT_PAGE_NAMES.ExternalOffersDetail);
        }

        if (isUpcomingOffer) {
          setEventPageName(EVENT_PAGE_NAMES.UpcomingOfferDetail);
        }

        onChallengeIcentiveSelect(isQuestChallengeIcentive);
        navigate(`${routes.rewardsOffers}/${navigateID}`);
      }
    };

  return {
    onPromoCodeRedeemSuccess,
    fetchOffers,
    offers,
    offersInCart,
    loyaltyId,
    selectedIncentive,
    handleClick,
  };
};
